let ctx = null;

export const ensureCtx = () => {
  if (!ctx) {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (ctx.state === "suspended") ctx.resume();
  return ctx;
};

let analyser = null;
let freqData = null;

export const getAnalyser = () => {
  const c = ensureCtx();
  if (!analyser) {
    analyser = c.createAnalyser();
    analyser.fftSize = 256;
    analyser.smoothingTimeConstant = 0.78;
    analyser.connect(c.destination);
    freqData = new Uint8Array(analyser.frequencyBinCount);
  }
  return analyser;
};

export const connectElement = (el) => {
  if (!el || el.__rotuSrc) return;
  const src = ensureCtx().createMediaElementSource(el);
  src.connect(getAnalyser());
  el.__rotuSrc = true;
};

export const getBassLevel = () => {
  if (!analyser) return 0;
  analyser.getByteFrequencyData(freqData);
  let sum = 0;
  for (let i = 1; i <= 6; i++) sum += freqData[i];
  return Math.min(1, (sum / 6 / 255) * 1.7);
};

export function playSqueak() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(900, t);
    osc.frequency.exponentialRampToValueAtTime(2600, t + 0.09);
    osc.frequency.exponentialRampToValueAtTime(1400, t + 0.16);
    gain.gain.setValueAtTime(0.001, t);
    gain.gain.exponentialRampToValueAtTime(0.12, t + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.2);
  } catch (e) {}
}

export function playZap() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(1800, t);
    osc.frequency.exponentialRampToValueAtTime(90, t + 0.14);
    gain.gain.setValueAtTime(0.18, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.16);
    osc.connect(gain).connect(c.destination);
    osc.start(t);
    osc.stop(t + 0.18);

    const buf = c.createBuffer(1, c.sampleRate * 0.08, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const noise = c.createBufferSource();
    noise.buffer = buf;
    const nGain = c.createGain();
    nGain.gain.setValueAtTime(0.1, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    noise.connect(nGain).connect(c.destination);
    noise.start(t);

    const growl = c.createOscillator();
    const gGain = c.createGain();
    growl.type = "sawtooth";
    growl.frequency.setValueAtTime(85, t);
    growl.frequency.exponentialRampToValueAtTime(32, t + 0.3);
    gGain.gain.setValueAtTime(0.22, t);
    gGain.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
    growl.connect(gGain).connect(c.destination);
    growl.start(t);
    growl.stop(t + 0.34);
  } catch (e) {}
}

export function playKillSound() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    const variant = Math.floor(Math.random() * 4);
    const pitch = 0.8 + Math.random() * 0.5;
    const tone = (type, f0, f1, dur, vol) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(f0 * pitch, t);
      if (f1) osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t + dur * 0.85);
      g.gain.setValueAtTime(vol, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      osc.connect(g).connect(c.destination);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    };

    if (variant === 0) tone("square", 1800, 90, 0.16, 0.18);
    else if (variant === 1) tone("sine", 300, 55, 0.24, 0.32);
    else if (variant === 2) {
      tone("triangle", 620, 0, 0.35, 0.2);
      tone("triangle", 933, 0, 0.28, 0.12);
    } else tone("sine", 120, 32, 0.28, 0.5);

    const buf = c.createBuffer(1, c.sampleRate * 0.1, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const noise = c.createBufferSource();
    noise.buffer = buf;
    const nGain = c.createGain();
    nGain.gain.setValueAtTime(0.12, t);
    nGain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    noise.connect(nGain).connect(c.destination);
    noise.start(t);

    tone("sawtooth", 85, 32, 0.32, 0.2);
  } catch (e) {}
}

export function playWhisper() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    const dur = 1.2 + Math.random();
    const buf = c.createBuffer(1, c.sampleRate * dur, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = "bandpass";
    f.frequency.setValueAtTime(300 + Math.random() * 900, t);
    f.frequency.exponentialRampToValueAtTime(1500 + Math.random() * 1500, t + dur);
    f.Q.value = 6;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05 + Math.random() * 0.04, t + dur * 0.4);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(f).connect(g).connect(c.destination);
    src.start(t);
  } catch (e) {}
}

export function playSiren() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    const osc = c.createOscillator();
    const g = c.createGain();
    const f = c.createBiquadFilter();
    osc.type = "sawtooth";
    for (let i = 0; i < 3; i++) {
      osc.frequency.setValueAtTime(320, t + i * 0.9);
      osc.frequency.linearRampToValueAtTime(760, t + i * 0.9 + 0.45);
      osc.frequency.linearRampToValueAtTime(320, t + i * 0.9 + 0.9);
    }
    f.type = "lowpass";
    f.frequency.value = 2400;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.09, t + 0.1);
    g.gain.setValueAtTime(0.09, t + 2.5);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    osc.connect(f).connect(g).connect(c.destination);
    osc.start(t);
    osc.stop(t + 2.9);
  } catch (e) {}
}

export function playSparkle() {
  try {
    const c = ensureCtx();
    const t = c.currentTime;
    [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => {
      const osc = c.createOscillator();
      const g = c.createGain();
      osc.type = "triangle";
      osc.frequency.value = f;
      const s = t + i * 0.09;
      g.gain.setValueAtTime(0.0001, s);
      g.gain.exponentialRampToValueAtTime(0.14, s + 0.03);
      g.gain.exponentialRampToValueAtTime(0.0001, s + 0.35);
      osc.connect(g).connect(c.destination);
      osc.start(s);
      osc.stop(s + 0.4);
    });
  } catch (e) {}
}

let ambienceStarted = false;
export const startAmbience = () => {
  if (ambienceStarted) return;
  ambienceStarted = true;
  const loop = () => {
    if (Math.random() < 0.55) playWhisper();
    setTimeout(loop, 12000 + Math.random() * 22000);
  };
  setTimeout(loop, 6000);
};

const BASS_NOTES = [55, 55, 0, 55, 0, 65.41, 55, 0, 55, 0, 49, 55, 0, 58.27, 0, 73.42];

export class SynthLoop {
  constructor() {
    this.timer = null;
    this.step = 0;
    this.gain = null;
    this.volume = 0.6;
  }

  start() {
    const c = ensureCtx();
    if (this.timer) return;
    this.gain = c.createGain();
    this.gain.gain.value = this.volume;
    this.gain.connect(getAnalyser());
    this.step = 0;
    this.nextTime = c.currentTime + 0.05;
    this.timer = setInterval(() => this.schedule(c), 60);
  }

  schedule(c) {
    const stepDur = 60 / 132 / 4;
    while (this.nextTime < c.currentTime + 0.18) {
      const s = this.step % 16;
      const t = this.nextTime;
      if (s % 4 === 0) this.kick(c, t);
      if (s % 4 === 2) this.hat(c, t, 0.5);
      else if (s % 2 === 1) this.hat(c, t, 0.2);
      const note = BASS_NOTES[s];
      if (note) this.bass(c, t, note);
      if (s === 0 && Math.floor(this.step / 16) % 4 === 3) this.siren(c, t);
      this.step++;
      this.nextTime += stepDur;
    }
  }

  kick(c, t) {
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.frequency.setValueAtTime(160, t);
    osc.frequency.exponentialRampToValueAtTime(42, t + 0.12);
    g.gain.setValueAtTime(0.9, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.24);
    osc.connect(g).connect(this.gain);
    osc.start(t);
    osc.stop(t + 0.26);
  }

  hat(c, t, level) {
    const buf = c.createBuffer(1, c.sampleRate * 0.04, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / d.length);
    const src = c.createBufferSource();
    src.buffer = buf;
    const f = c.createBiquadFilter();
    f.type = "highpass";
    f.frequency.value = 7000;
    const g = c.createGain();
    g.gain.value = 0.16 * level;
    src.connect(f).connect(g).connect(this.gain);
    src.start(t);
  }

  bass(c, t, freq) {
    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    const f = c.createBiquadFilter();
    f.type = "lowpass";
    f.frequency.setValueAtTime(600, t);
    f.frequency.exponentialRampToValueAtTime(180, t + 0.16);
    const g = c.createGain();
    g.gain.setValueAtTime(0.32, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.19);
    osc.connect(f).connect(g).connect(this.gain);
    osc.start(t);
    osc.stop(t + 0.2);
  }

  siren(c, t) {
    const osc = c.createOscillator();
    osc.type = "triangle";
    osc.frequency.setValueAtTime(440, t);
    osc.frequency.linearRampToValueAtTime(880, t + 0.4);
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.05, t + 0.1);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.5);
    osc.connect(g).connect(this.gain);
    osc.start(t);
    osc.stop(t + 0.5);
  }

  setVolume(v) {
    this.volume = v;
    if (this.gain) this.gain.gain.value = v;
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.gain) {
      try {
        this.gain.disconnect();
      } catch (e) {}
      this.gain = null;
    }
  }
}
